import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { supabase } from '@/lib/supabase/client';

// Platform Imports
import { useBusinessConfig } from './hooks/useBusinessConfig';
import { OnboardingFlow } from './components/OnboardingFlow';
import { MedicationLogUI } from './components/vbs/forms/MedicationLogUI';

// Existing Imports
import { Home } from './components/Home';
import BookingForm from './components/BookingForm';
import { AdminDashboard } from './components/AdminDashboard';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { QuoteView } from './components/vbs/public/QuoteView';
import { InteractiveQuoteView } from './components/vbs/public/InteractiveQuoteView';
import { QuoteRequest } from './components/QuoteRequest';
import { NotFound } from './components/NotFound';
import { NDISVerificationForm } from './components/NDISVerificationForm';
import { JobsDashboard } from './components/JobsDashboard';
import { LoginPage } from './components/LoginPage';
import { TaskTemplateEngine } from './components/vbs/forms/TaskTemplateEngine';
import { UpgradePage } from './components/UpgradePage';
import { SuperAdminDashboard } from './components/SuperAdminDashboard';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsOfService } from './components/TermsOfService';

function App() {
  const { config, loading } = useBusinessConfig();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Check if onboarding was skipped (permanent feature)
  const onboardingSkipped = typeof window !== 'undefined' && localStorage.getItem('onboardingSkipped') === 'true';

  // Check Supabase auth session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Session error:', error.message);
        }

        if (session) {
          setIsAuthenticated(true);
          setIsAdmin(true);
        } else {
          setIsAuthenticated(false);
          setIsAdmin(false);
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        setIsAuthenticated(false);
        setIsAdmin(false);
      } finally {
        setAuthLoading(false);
      }
    };

    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      if (session) {
        setIsAuthenticated(true);
        setIsAdmin(true);
      } else {
        setIsAuthenticated(false);
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading || authLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
    </div>
  );

  return (
    <BrowserRouter>
      <Layout isAdmin={isAdmin} setIsAdmin={setIsAdmin}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/booking" element={<BookingForm />} />
          <Route path="/quote-request" element={<QuoteRequest />} />
          <Route path="/quote/:quoteId" element={<QuoteView />} />
          <Route path="/interactive-quote/:quoteId" element={<InteractiveQuoteView />} />
          <Route path="/ndis-verify" element={<NDISVerificationForm />} />
          <Route path="/jobs" element={<JobsDashboard />} />
          <Route path="/upgrade" element={<UpgradePage />} />
          <Route path="/saas-admin" element={<SuperAdminDashboard />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />

          {/* Business Onboarding - REQUIRED if not completed (but not on login page) */}
          {(!config || !config.onboarding_completed) && !onboardingSkipped ? (
            <>
              <Route path="/login" element={<LoginPage />} />
              <Route path="*" element={<OnboardingFlow />} />
            </>
          ) : (
            <>
              {/* Login route when onboarding is complete or skipped */}
              <Route path="/login" element={isAuthenticated ? <Navigate to="/admin" /> : <LoginPage />} />

              {/* Protected Admin Routes */}
              <Route element={<ProtectedRoute isAdmin={isAdmin || onboardingSkipped}><Outlet /></ProtectedRoute>}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/staff-tool" element={
                  config?.biz_type === 'support_worker'
                    ? <MedicationLogUI />
                    : <TaskTemplateEngine />
                } />
              </Route>
            </>
          )}

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
      <Toaster position="top-center" />
    </BrowserRouter>
  );
}

export default App;